import * as React from 'react';
import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { API } from 'aws-amplify';
import * as mutations from '../../../../graphql/mutations';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import { useToast } from '../../../components/ui/toast';

interface ChangeRoleModalProps {
  open: boolean;
  onClose: () => void;
  orgMemberID: string;
  currentRole: string;
  userName: string;
  onRoleChanged: (newRole: string) => void;
}

const ROLE_OPTIONS = [
  { label: 'Member', value: 'member' },
  { label: 'Moderator', value: 'moderator' },
  { label: 'Admin', value: 'admin' },
];

export function ChangeRoleModal({
  open,
  onClose,
  orgMemberID,
  currentRole,
  userName,
  onRoleChanged,
}: ChangeRoleModalProps) {
  const toast = useToast();
  const [selectedRole, setSelectedRole] = useState(currentRole || 'member');
  const [isSaving, setIsSaving] = useState(false);

  React.useEffect(() => {
    if (open) {
      setSelectedRole(currentRole || 'member');
    }
  }, [open, currentRole]);

  const handleSave = async () => {
    if (selectedRole === currentRole) {
      onClose();
      return;
    }

    setIsSaving(true);
    try {
      await API.graphql({
        query: mutations.updateOrgMember,
        variables: {
          input: {
            id: orgMemberID,
            memberType: selectedRole,
          },
        },
      });
      toast.success(`${userName}'s role updated to ${selectedRole}`);
      onRoleChanged(selectedRole);
      onClose();
    } catch (err) {
      console.error('Error changing role:', err);
      toast.error('Failed to update role. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="max-w-[420px] rounded-2xl">
        <DialogHeader>
          <DialogTitle>Change Role</DialogTitle>
          <DialogDescription className="sr-only">
            Change the role for {userName}
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          <label className="text-xs text-muted-foreground mb-2 block">
            Role
          </label>
          <Select value={selectedRole} onValueChange={setSelectedRole}>
            <SelectTrigger className="w-full h-[42px] rounded-xl text-[15px] font-medium border-border/60">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {ROLE_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <DialogFooter className="flex-row gap-4 sm:flex-row sm:space-x-0">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isSaving}
            className="flex-1 h-12 rounded-full text-base font-bold"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={isSaving}
            className="flex-1 h-12 rounded-full text-base font-bold bg-[hsl(var(--primary-dark))] hover:bg-[hsl(var(--primary-dark))]/90 text-white"
          >
            {isSaving && <Loader2 className="size-4 animate-spin mr-2" />}
            Save changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
